import { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import Navbar from '@/components/layout/Navbar';
import Footer from '@/components/layout/Footer';
import { Calendar, ArrowLeft, Tag } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const FONT_HEADING = "'Cormorant Garamond', Cambria, Georgia, serif";
const FONT_BODY = "'Jost', sans-serif";

export default function BlogPostPage() {
  const { slug } = useParams();
  const [post, setPost] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchPost = async () => {
      try {
        const res = await fetch(`${API}/blog/posts/${slug}`);
        if (res.ok) setPost(await res.json());
      } catch {}
      setLoading(false);
    };
    fetchPost();
  }, [slug]);

  useEffect(() => {
    if (post) {
      document.title = post.seo_title || `${post.title} | Sàwai Capital`;
      const metaDesc = document.querySelector('meta[name="description"]');
      if (metaDesc) metaDesc.content = post.seo_description || post.excerpt || '';
    }
    return () => { document.title = "Sàwai Capital Dealroom — India's Private Deal Network"; };
  }, [post]);

  if (loading) return <div className="min-h-screen bg-[#1A1A1A] flex items-center justify-center"><div className="w-8 h-8 border-2 border-[#A0743A] border-t-transparent rounded-full animate-spin" /></div>;
  if (!post) return <div className="min-h-screen bg-[#1A1A1A] flex items-center justify-center"><p className="text-[#999]">Post not found.</p></div>;

  return (
    <div className="min-h-screen bg-[#1A1A1A]" data-testid="blog-post-page">
      <Navbar />
      <article className="max-w-3xl mx-auto px-6 md:px-12 py-16 pt-28">
        <Link to="/blog" className="flex items-center gap-2 text-[#A0743A] text-sm hover:text-[#C9A96E] transition-colors mb-10" style={{ fontFamily: FONT_BODY }}>
          <ArrowLeft size={16} /> Back to Blog
        </Link>

        {post.cover_image && <img src={post.cover_image} alt={post.title} className="w-full h-auto mb-8" />}

        <div className="flex items-center gap-4 mb-6">
          {post.tags?.map(tag => (
            <span key={tag} className="text-[#A0743A] text-[10px] uppercase tracking-wider font-semibold flex items-center gap-1" style={{ fontFamily: FONT_BODY }}><Tag size={10} /> {tag}</span>
          ))}
        </div>

        <h1 className="text-3xl md:text-5xl text-[#FAF8F5] mb-6 tracking-tight leading-[1.15]" style={{ fontFamily: FONT_HEADING }}>{post.title}</h1>

        <div className="flex items-center gap-4 text-[#555] text-sm mb-12 pb-8 border-b border-[#A0743A]/10" style={{ fontFamily: FONT_BODY }}>
          <Calendar size={14} />
          {post.published_at ? new Date(post.published_at).toLocaleDateString('en-IN', { day: 'numeric', month: 'long', year: 'numeric' }) : ''}
        </div>

        <div
          className="prose prose-lg max-w-none blog-content"
          style={{ fontFamily: FONT_BODY }}
          data-testid="blog-post-content"
          dangerouslySetInnerHTML={{ __html: post.content }}
        />

        <style>{`
          /* Base text color — forced to cream so pasted inline colors (e.g. from Word/Docs) don't make text invisible on dark background */
          .blog-content, .blog-content * { color: #FAF8F5 !important; background-color: transparent !important; }
          .blog-content { font-family: ${FONT_BODY}; }
          .blog-content h1, .blog-content h2, .blog-content h3, .blog-content h4 { font-family: ${FONT_HEADING}; color: #A0743A !important; margin-top: 2em; margin-bottom: 0.5em; line-height: 1.25; }
          .blog-content h2 { font-size: 1.75rem; }
          .blog-content h3 { font-size: 1.35rem; }
          .blog-content h4 { font-size: 1.15rem; }
          .blog-content p { color: #FAF8F5 !important; opacity: 0.88; line-height: 1.8; margin-bottom: 1.25em; font-size: 1.05rem; }
          .blog-content a { color: #A0743A !important; text-decoration: underline; }
          .blog-content a:hover { color: #C9A96E !important; }
          .blog-content ul, .blog-content ol { color: #FAF8F5 !important; opacity: 0.88; padding-left: 1.5em; margin-bottom: 1.25em; }
          .blog-content li { margin-bottom: 0.5em; }
          .blog-content blockquote { border-left: 3px solid #A0743A; padding-left: 1em; color: #C9A96E !important; font-style: italic; opacity: 1; }
          .blog-content blockquote * { color: #C9A96E !important; }
          .blog-content img { width: 100%; margin: 1.5em 0; }
          .blog-content strong, .blog-content b { color: #FAF8F5 !important; font-weight: 700; }
          .blog-content em, .blog-content i { color: #FAF8F5 !important; }
          .blog-content code { background: rgba(160, 116, 58, 0.15) !important; color: #C9A96E !important; padding: 2px 6px; border-radius: 2px; font-size: 0.9em; }
          .blog-content pre { background: rgba(0,0,0,0.3) !important; padding: 1em; border-left: 3px solid #A0743A; overflow-x: auto; }
          .blog-content pre code { background: transparent !important; }
          .blog-content hr { border-color: rgba(160, 116, 58, 0.2); margin: 2em 0; }
          .blog-content table { width: 100%; border-collapse: collapse; margin: 1.5em 0; }
          .blog-content th, .blog-content td { border: 1px solid rgba(160, 116, 58, 0.2); padding: 0.5em 0.75em; }
          .blog-content th { background: rgba(160, 116, 58, 0.1) !important; color: #C9A96E !important; font-weight: 600; }
        `}</style>
      </article>
      <Footer />
    </div>
  );
}
